import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import authService from '../../services/authService';
import type { CompanyUser } from '../../services/authService';
import { CheckCircleOutlineOutlined } from '@mui/icons-material';

interface ProtectedCompanyRouteProps {
  children: React.ReactNode;
}
interface ResetPasswordFormProps {
  email: string;
  setVisible: (value: React.SetStateAction<boolean>) => void;
}
const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ email, setVisible }) => {
  const [resetPasswordEmail, setResetPasswordEmail] = useState(email);
  const [sendPasswordResetEmail, setSendPasswordResetEmail] = useState(false);
  const [resetPasswordEmailError, setResetPasswordEmailError] = useState<string | null>(null);
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await authService.resetPassword(resetPasswordEmail);
      setSendPasswordResetEmail(true);
    } catch (error) {
      setResetPasswordEmailError('パスワードリセットメールの送信に失敗しました');
    }
  };
  if (sendPasswordResetEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg p-6">
          <div className="flex justify-center mb-2">
            <CheckCircleOutlineOutlined
              sx={{
                fontSize: 64,
                color: 'primary.main',
              }}
            />
          </div>
          <div className="text-center">
            <p>パスワードリセットメールを送信しました</p>
          </div>
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              <button
                onClick={() => setVisible(false)}
                className="text-blue-600 hover:text-blue-500"
              >
                ログイン画面に戻る
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 ">
      <div className="bg-white shadow-lg rounded-lg p-6">
        {resetPasswordEmailError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {resetPasswordEmailError}
          </div>
        )}
        <div>メールアドレスを入力してください</div>
        <div className="mt-4">
          <input
            type="email"
            value={resetPasswordEmail}
            onChange={(e) => setResetPasswordEmail(e.target.value)}
            required
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="mt-4">
          <button
            disabled={resetPasswordEmail === ''}
            onClick={handleResetPassword}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
          >
            パスワードをリセット
          </button>
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              <button
                onClick={() => setVisible(false)}
                className="text-blue-600 hover:text-blue-500"
              >
                ログイン画面に戻る
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProtectedCompanyRoute: React.FC<ProtectedCompanyRouteProps> = ({ children }) => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [companyName, setCompanyName] = useState<string>('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const setupTenant = useCallback(async () => {
    if (!companyId) return false;

    try {
      const tenantResult = await authService.setupTenantByCompanyId(companyId);
      if (tenantResult.success) {
        if (tenantResult.companyName) {
          setCompanyName(tenantResult.companyName);
        }
        return true;
      } else {
        setError(tenantResult.error || '企業情報の取得に失敗しました');
        return false;
      }
    } catch (err) {
      setError('企業情報の取得中にエラーが発生しました');
      return false;
    }
  }, [companyId]);

  const checkAccess = useCallback(async () => {
    try {
      // まずテナント情報を設定
      const tenantSetup = await setupTenant();
      if (!tenantSetup) {
        setLoading(false);
        return;
      }

      const accessCheck = await authService.checkCompanyAccess(companyId!);

      if (accessCheck.hasAccess) {
        setIsAuthenticated(true);
        setShowLogin(false);
      } else {
        setIsAuthenticated(false);
        setShowLogin(true);
      }
    } catch (err) {
      setError('アクセス権限の確認に失敗しました');
      setShowLogin(true);
    } finally {
      setLoading(false);
    }
  }, [companyId, setupTenant]);

  useEffect(() => {
    if (!companyId) {
      navigate('/admin');
      return;
    }

    checkAccess();

    // 認証状態の監視
    const unsubscribe = authService.onAuthStateChanged(
      async (firebaseUser: User | null, _companyUser: CompanyUser | null) => {
        if (firebaseUser) {
          // firebaseUserが存在すれば、companyUserがnullでも再取得を試みる
          // （リロード時はcompanyUserがnullになるため）
          const accessCheck = await authService.checkCompanyAccess(companyId);
          if (accessCheck.hasAccess) {
            setIsAuthenticated(true);
            setShowLogin(false);
          } else {
            setIsAuthenticated(false);
            setShowLogin(true);
            setError('この企業への権限がありません');
          }
        } else {
          setIsAuthenticated(false);
          setShowLogin(true);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, navigate, checkAccess]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setError(null);

    try {
      const result = await authService.signIn(email, password);

      if (result.success) {
        // ログイン成功後、再度アクセス権限をチェック
        const accessCheck = await authService.checkCompanyAccess(companyId!);
        if (accessCheck.hasAccess) {
          setIsAuthenticated(true);
          setShowLogin(false);
        } else {
          setError('この企業への権限がありません');
          await authService.signOutUser();
        }
      } else {
        setError(result.error || 'ログインに失敗しました');
      }
    } catch (err: any) {
      setError(`ログインエラー: ${err.message}`);
    } finally {
      setLoginLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">認証情報を確認しています...</p>
        </div>
      </div>
    );
  }

  if (showResetPassword) {
    return <ResetPasswordForm email={email} setVisible={setShowResetPassword} />;
  }

  if (showLogin || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              企業管理画面へログイン
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {companyName ? (
                <>
                  企業: <strong>{companyName}</strong>
                </>
              ) : (
                <>
                  企業ID: <strong>{companyId}</strong>
                </>
              )}
            </p>
          </div>

          <div className="bg-white shadow-lg rounded-lg p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">メールアドレス</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your-email@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">パスワード</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
              >
                {loginLoading ? '認証中...' : 'ログイン'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                招待を受けていませんか？{' '}
                <button
                  onClick={() => navigate('/admin')}
                  className="text-blue-600 hover:text-blue-500"
                >
                  管理画面トップへ
                </button>
              </p>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                <button
                  onClick={() => setShowResetPassword(true)}
                  className="text-blue-600 hover:text-blue-500"
                >
                  パスワードを忘れた場合はこちら
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* メインコンテンツ - ヘッダーは子コンポーネントで管理 */}
      <main>{children}</main>
    </div>
  );
};

export default ProtectedCompanyRoute;
